import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  CircularProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  CalendarMonth,
  CheckCircle,
  Extension,
  GppMaybe,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import { CommonDialog } from "../../../components/common/CommonDialog";
import { useOrganizationContext } from "../../../contexts/OrganizationContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import { oids } from "../../../configs/Settings";
import { GetSchedule, PatchSchedule } from "../../../services/fhir";
import { GetContract } from "../../../services/fhir/contract/GetContract";
import { PutContract } from "../../../services/fhir/contract/PutContract";
import { PatchOrganizationActive } from "../../../services/fhir/organization/PatchOrganizationActive";
import { SendEmailCancelClinic } from "../../../services/gmail/SendEmailCancelClinic";
import { isFluxPayContract } from "../../../utils/contract";
import { useFluxPayContractContext } from "../../../contexts/FluxPayContractContext";

const STEPS = ["Agendas", "Extensões", "Confirmação"];

type ScheduleRow = { id: string };
type ExtensionRow = { id: string; name: string };
type SubscriptionRow = { id: string };

const EXTENSION_TITLES: Record<string, string> = {
  fluxpayplan: "FluxPay",
  "pos-atendimento": "FluxZap",
  pos_atendimento: "FluxZap",
  service_plan: "FluxZap",
  fluxzap: "FluxZap",
  post_attendance: "FluxZap",
  questionnaires: "Questionários de Saúde",
  fluxsigner: "FluxSigner - Assinatura Digital",
  healthplan: "Emissão de Guias TISS",
  teleconsultoria: "Teleconsultoria",
  prontoatendimento: "Pronto Atendimento",
  crm50: "CRM",
  crm100: "CRM",
  secretary_agent: "Secretária IA",
  medical_reports_agent: "Agente de relatórios médicos",
  guidance_agent: "Agente de Orientações ao paciente",
  discussion_case_agent: "Agente de discussão de casos",
};

// Normaliza para minúsculo antes de comparar, já que o mesmo código aparece
// com casing diferente dependendo de quem criou o contrato.
function resolveExtensionTitle(codes: Array<string | undefined>): string | undefined {
  for (const code of codes) {
    const normalized = code?.trim().toLowerCase();
    if (normalized && EXTENSION_TITLES[normalized]) {
      return EXTENSION_TITLES[normalized];
    }
  }
  return undefined;
}

function extensionName(resource: any): string {
  const identifierCodes = (resource?.identifier || []).map((item: any) => item?.value);
  const typeCodes = (resource?.type?.coding || []).flatMap((coding: any) => [
    coding?.code,
    coding?.value,
  ]);
  // Alguns contratos (ex.: FluxZap) só carregam o código real dentro de term[].identifier.value.
  const termCodes = (resource?.term || []).map(
    (term: any) => term?.identifier?.value,
  );

  return (
    resolveExtensionTitle([...identifierCodes, ...typeCodes, ...termCodes]) ||
    (isFluxPayContract(resource) ? "FluxPay" : undefined) ||
    resource?.type?.coding?.[0]?.display ||
    "FluxZap"
  );
}

function isBaseSubscription(resource: any): boolean {
  return resource?.identifier?.some(
    (item: any) => item?.value === "subscription",
  );
}

export function CancelClinicDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { organization, refetchOrganization, refetchOrganizationList } =
    useOrganizationContext();
  const { roles, setAccess, getRolesArray, user } = useSessionContext();
  const navigate = useNavigate();
  const { contractId: fluxPayContractId } = useFluxPayContractContext();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [extensions, setExtensions] = useState<ExtensionRow[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);

  useEffect(() => {
    if (!open || !organization?.id) return;
    setStep(0);
    let cancelled = false;

    async function load() {
      setLoading(true);
      try {
        const [scheduleBundle, contractBundle] = await Promise.all([
          GetSchedule({
            active: true,
            organization: `${oids.cnpj}-${organization?.id?.split("-").pop()}`,
          }),
          GetContract({
            organization: organization?.id,
            status: "executed",
            count: 100,
            sort: true,
          }),
        ]);
        if (cancelled) return;

        setSchedules(
          (scheduleBundle?.entry || [])
            .filter((entry: any) => entry?.resource?.resourceType === "Schedule")
            .map((entry: any) => ({ id: entry.resource.id })),
        );

        const contractResources = (contractBundle?.entry || [])
          .map((entry: any) => entry?.resource)
          .filter(Boolean);

        // A assinatura base (fluxPayPlan/subscription) não é uma "extensão" que o
        // usuário ativa/desativa individualmente, mas também precisa ser cancelada
        // junto com a clínica — por isso vai separada, não para a lista de extensões.
        setSubscriptions(
          contractResources
            .filter((resource: any) => isBaseSubscription(resource))
            .map((resource: any) => ({ id: resource.id })),
        );
        setExtensions(
          contractResources
            .filter((resource: any) => !isBaseSubscription(resource))
            .map((resource: any) => ({
              id: resource.id,
              name:
                resource.id === fluxPayContractId
                  ? "FluxPay"
                  : extensionName(resource),
            })),
        );
      } catch (error) {
        console.error("CancelClinicDialog load", error);
        toast.error("Não foi possível carregar agendas e extensões.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, organization?.id]);

  useEffect(() => {
    if (!fluxPayContractId) return;
    setExtensions((current) =>
      current.map((item) =>
        item.id === fluxPayContractId ? { ...item, name: "FluxPay" } : item,
      ),
    );
  }, [fluxPayContractId]);

  const cancelSchedules = async () => {
    setBusy(true);
    try {
      await Promise.all(
        schedules.map((item) =>
          PatchSchedule(item.id, [
            { op: "replace", path: "/active", value: "false" },
          ]),
        ),
      );
      setSchedules([]);
      toast.success("Agendas canceladas.");
    } catch (error) {
      console.error("cancelSchedules", error);
      toast.error("Não foi possível cancelar as agendas.");
    } finally {
      setBusy(false);
    }
  };

  const disableAllExtensions = async () => {
    setBusy(true);
    try {
      await Promise.all(
        extensions.map((item) => PutContract({ contractId: item.id })),
      );
      setExtensions([]);
      toast.success("Extensões desabilitadas.");
    } catch (error) {
      console.error("disableAllExtensions", error);
      toast.error("Não foi possível desabilitar as extensões.");
    } finally {
      setBusy(false);
    }
  };

  const confirmDeactivation = async () => {
    if (!organization?.id) return;
    setBusy(true);
    try {
      // Cancela a assinatura base junto com a clínica, se houver uma ativa.
      if (subscriptions.length > 0) {
        await Promise.all(
          subscriptions.map((item) => PutContract({ contractId: item.id })),
        );
      }
      await PatchOrganizationActive(organization.id);
      toast.success(
        subscriptions.length > 0
          ? "Clínica e assinatura desativadas."
          : "Clínica desativada.",
      );

      // Avisa o time Fluxmed por e-mail sobre o encerramento, com a referência
      // da clínica. Isolado em try/catch próprio: se o e-mail falhar, a clínica
      // já foi desativada com sucesso e o fluxo não deve ser interrompido.
      try {
        const organizationName =
          organization?.alias?.[0] && organization.alias[0] !== "null"
            ? organization.alias[0]
            : organization?.name;
        await SendEmailCancelClinic(
          { name: user?.name },
          `${organizationName || ""} (Ref: ${organization.id})`,
          user?.username,
        );
      } catch (emailError) {
        console.error("SendEmailCancelClinic", emailError);
      }

      refetchOrganization();
      // organizationList é cacheada no contexto global e alimenta os seletores de
      // clínica (dropdown do header, ViewDialog etc). Sem isso, a clínica cancelada
      // continua aparecendo como ativa até um reload de página.
      refetchOrganizationList();


      const canceledOrgSuffix = organization.id?.split("-")?.pop();
      const remainingRoles = (roles || []).filter((role: any) => {
        if (role?.isInvited) return false;
        const roleOrgSuffix = role?.organization?.reference?.split("-")?.[1];
        return roleOrgSuffix !== canceledOrgSuffix;
      });

      if (remainingRoles.length > 0) {
        setAccess({
          type: "professional",
          organizationId:
            remainingRoles[0]?.organization?.reference?.split("-")[1],
          roles: getRolesArray(remainingRoles[0]),
        });
      } else {
        setAccess({ type: "patient" });
      }
      navigate("/");
      onClose();
    } catch (error) {
      console.error("confirmDeactivation", error);
      toast.error("Não foi possível desativar a clínica.");
    } finally {
      setBusy(false);
    }
  };

  const blockedBySchedules = step === 0 && schedules.length > 0;
  const blockedByExtensions = step === 1 && extensions.length > 0;

  return (
    <CommonDialog
      isOpen={open}
      handleClose={onClose}
      sheetTitle="Cancelamento da clínica"
      sheetSize="medium"
      paperSx={{ minHeight: 420 }}
    >
      <Stack spacing={2.5} sx={{ px: { xs: 0.5, sm: 1.5 }, py: 0.5, minWidth: { sm: 480 } }}>
        <Typography variant="h6" fontWeight={700}>
          Cancelamento da clínica
        </Typography>

        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        ) : step === 0 ? (
          <Stack spacing={2} alignItems="center">
            <Stack
              alignItems="center"
              spacing={1.5}
              sx={(theme) => ({
                width: "100%",
                borderRadius: 3,
                px: 3,
                py: 3,
                bgcolor: alpha(
                  schedules.length > 0
                    ? theme.palette.warning.main
                    : theme.palette.success.main,
                  0.08,
                ),
                border: "1px solid",
                borderColor: alpha(
                  schedules.length > 0
                    ? theme.palette.warning.main
                    : theme.palette.success.main,
                  0.25,
                ),
              })}
            >
              <Avatar
                sx={(theme) => ({
                  width: 56,
                  height: 56,
                  bgcolor: alpha(
                    schedules.length > 0
                      ? theme.palette.warning.main
                      : theme.palette.success.main,
                    0.15,
                  ),
                  color:
                    schedules.length > 0
                      ? theme.palette.warning.dark
                      : theme.palette.success.dark,
                })}
              >
                {schedules.length > 0 ? <CalendarMonth /> : <CheckCircle />}
              </Avatar>

              {schedules.length > 0 ? (
                <>
                  <Typography variant="h3" fontWeight={800} lineHeight={1}>
                    {schedules.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {schedules.length === 1
                      ? "agenda ativa precisa ser cancelada"
                      : "agendas ativas precisam ser canceladas"}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Nenhuma agenda ativa. Você já pode continuar.
                </Typography>
              )}
            </Stack>

            {schedules.length > 0 && (
              <Button
                variant="contained"
                color="warning"
                disabled={busy}
                onClick={() => void cancelSchedules()}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, px: 3 }}
              >
                Cancelar todas as agendas
              </Button>
            )}
          </Stack>
        ) : step === 1 ? (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              Extensões ativas nesta clínica. Desabilite as que não forem mais necessárias.
            </Typography>
            {extensions.length === 0 ? (
              <Stack
                alignItems="center"
                spacing={1}
                sx={(theme) => ({
                  borderRadius: 3,
                  px: 3,
                  py: 3,
                  bgcolor: alpha(theme.palette.success.main, 0.08),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.success.main, 0.25),
                })}
              >
                <CheckCircle color="success" />
                <Typography variant="body2" color="text.secondary">
                  Nenhuma extensão ativa.
                </Typography>
              </Stack>
            ) : (
              <>
                <Stack spacing={1} sx={{ maxHeight: 220, overflow: "auto" }}>
                  {extensions.map((item) => (
                    <Stack
                      key={item.id}
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{
                        borderRadius: 2.5,
                        px: 2,
                        py: 1,
                        bgcolor: "action.hover",
                      }}
                    >
                      <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.light" }}>
                        <Extension fontSize="small" />
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>
                        {item.name}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
                <Button
                  variant="contained"
                  color="warning"
                  disabled={busy}
                  onClick={() => void disableAllExtensions()}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2, alignSelf: "center", px: 3 }}
                >
                  Desabilitar todas as extensões
                </Button>
              </>
            )}
          </Stack>
        ) : (
          <Stack
            alignItems="center"
            spacing={1.5}
            sx={(theme) => ({
              borderRadius: 3,
              px: 3,
              py: 3,
              bgcolor: alpha(theme.palette.error.main, 0.06),
              border: "1px solid",
              borderColor: alpha(theme.palette.error.main, 0.2),
            })}
          >
            <Avatar sx={{ bgcolor: "error.light", color: "error.dark" }}>
              <GppMaybe />
            </Avatar>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              A clínica deixa de ficar ativa. Essa ação não apaga o histórico, mas encerra o uso da organização.
            </Typography>
            {subscriptions.length > 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center">
                A assinatura ativa desta clínica também será cancelada junto com a desativação.
              </Typography>
            )}
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between">
          <Button
            disabled={step === 0 || busy}
            onClick={() => setStep((current) => current - 1)}
            sx={{ textTransform: "none" }}
          >
            Voltar
          </Button>
          {step < 2 ? (
            <Button
              variant="contained"
              disabled={busy || blockedBySchedules || blockedByExtensions}
              onClick={() => setStep((current) => current + 1)}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Continuar
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              disabled={busy}
              onClick={() => void confirmDeactivation()}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              Confirmar exclusão
            </Button>
          )}
        </Stack>
      </Stack>
    </CommonDialog>
  );
}
